import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StarRating } from './StarRating';
import { uiClasses } from '../../../components/common/uiClasses';
import { useSubmitFeedback } from '../hooks/useFeedback';
import { FeedbackTopic } from '../feedbackApi';

interface FeedbackModalProps {
  onClose: () => void;
}

const TOPICS: FeedbackTopic[] = [
  'General',
  'Recipes',
  'Cookbooks',
  'Ingredients',
  'WeeklyPlanner',
  'ShoppingList',
];

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const { t } = useTranslation('feedback');
  const { mutate, isPending, isSuccess, isError } = useSubmitFeedback();

  const [topic, setTopic] = useState<FeedbackTopic | ''>('');
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [thingsToImprove, setThingsToImprove] = useState('');
  const [whatYouMiss, setWhatYouMiss] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [validationError, setValidationError] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!topic || stars === 0) {
      setValidationError(t('modal.required'));
      return;
    }
    setValidationError('');
    mutate({
      topic: topic as FeedbackTopic,
      stars,
      comment: comment || null,
      thingsToImprove: thingsToImprove || null,
      whatYouMiss: whatYouMiss || null,
      isAnonymous,
    });
  }

  if (isSuccess) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-success-title"
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden
        />
        <div
          className={`${uiClasses.surface} relative z-10 w-full max-w-md rounded-2xl p-6 text-center shadow-xl`}
        >
          <p className="text-4xl mb-3" aria-hidden>
            ✅
          </p>
          <h2
            id="feedback-success-title"
            className="text-xl font-bold text-[color:var(--color-text-primary)] mb-2"
          >
            {t('modal.successTitle')}
          </h2>
          <p className="text-[color:var(--color-text-secondary)] mb-6">
            {t('modal.successMessage')}
          </p>
          <button onClick={onClose} className={uiClasses.button.primary}>
            {t('modal.close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`${uiClasses.surface} relative z-10 w-full max-w-lg rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto`}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2
                id="feedback-modal-title"
                className="text-xl font-bold text-[color:var(--color-text-primary)]"
              >
                {t('modal.title')}
              </h2>
              <p className="text-sm text-[color:var(--color-text-secondary)] mt-1">
                {t('modal.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('modal.cancel')}
              className="ml-4 text-[color:var(--color-muted-text)] hover:text-[color:var(--color-text-primary)] text-xl leading-none"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Topic */}
            <div>
              <label className={uiClasses.label} htmlFor="feedback-topic">
                {t('modal.topic')}{' '}
                <span aria-hidden className="text-red-500">
                  *
                </span>
              </label>
              <select
                id="feedback-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value as FeedbackTopic)}
                className={uiClasses.select}
                required
              >
                <option value="">{t('modal.topicPlaceholder')}</option>
                {TOPICS.map((tp) => (
                  <option key={tp} value={tp}>
                    {t(`modal.topics.${tp}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Stars */}
            <div>
              <label className={`${uiClasses.label} mb-2 block`}>
                {t('modal.stars')}{' '}
                <span aria-hidden className="text-red-500">
                  *
                </span>
              </label>
              <StarRating value={stars} onChange={setStars} size="lg" label={t('modal.stars')} />
            </div>

            {/* Comment */}
            <div>
              <label className={uiClasses.label} htmlFor="feedback-comment">
                {t('modal.comment')}
              </label>
              <textarea
                id="feedback-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('modal.commentPlaceholder')}
                maxLength={1000}
                rows={3}
                className={uiClasses.textarea}
              />
            </div>

            {/* Things to improve */}
            <div>
              <label className={uiClasses.label} htmlFor="feedback-improve">
                {t('modal.thingsToImprove')}
              </label>
              <textarea
                id="feedback-improve"
                value={thingsToImprove}
                onChange={(e) => setThingsToImprove(e.target.value)}
                placeholder={t('modal.thingsToImprovePlaceholder')}
                maxLength={1000}
                rows={3}
                className={uiClasses.textarea}
              />
            </div>

            {/* What you miss */}
            <div>
              <label className={uiClasses.label} htmlFor="feedback-miss">
                {t('modal.whatYouMiss')}
              </label>
              <textarea
                id="feedback-miss"
                value={whatYouMiss}
                onChange={(e) => setWhatYouMiss(e.target.value)}
                placeholder={t('modal.whatYouMissPlaceholder')}
                maxLength={1000}
                rows={3}
                className={uiClasses.textarea}
              />
            </div>

            {/* Anonymous */}
            <div className="flex items-center gap-2">
              <input
                id="feedback-anonymous"
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="h-4 w-4 rounded border-[color:var(--color-border)] text-[color:var(--color-primary)] focus:ring-[color:var(--color-primary)]"
              />
              <label
                htmlFor="feedback-anonymous"
                className="text-sm text-[color:var(--color-text-secondary)]"
              >
                {t('modal.anonymous')}
              </label>
            </div>

            {/* Validation error */}
            {validationError && (
              <p role="alert" className="text-sm text-red-600">
                {validationError}
              </p>
            )}

            {/* Submit error */}
            {isError && (
              <p role="alert" className="text-sm text-red-600">
                {t('errors.submit')}
              </p>
            )}

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className={uiClasses.button.secondary}>
                {t('modal.cancel')}
              </button>
              <button type="submit" disabled={isPending} className={uiClasses.button.primary}>
                {isPending ? t('modal.submitting') : t('modal.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
